import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Loader2, Plus, Sparkles, Download } from "lucide-react";
import { ScenarioCard } from "@/components/scenarios/ScenarioCard";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth/useAuth";
import { usePowtoonAuth } from "@/lib/auth/PowtoonAuthContext";
import { powtoonBE } from "@/lib/powtoon-be/client";
import { resolveUsers } from "@/lib/powtoon-be/users";
import type { Scenario } from "@/lib/powtoon-be/types";
import { ImportLegacyModal } from "@/components/scenarios/ImportLegacyModal";
import { useTavusFaces } from "@/lib/scenarios/providers/tavus/options";
import { getAvailableProviders, canImportLegacySessions } from "@/lib/auth/orgSettings";

export function TrainerScenarioList() {
  const { hasPermission } = useAuth();
  const { user, orgDomain } = usePowtoonAuth();
  const canCreate = hasPermission("live-avatars.train");
  const canImport = canCreate && canImportLegacySessions(orgDomain);
  const queryClient = useQueryClient();
  const [importOpen, setImportOpen] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ["scenarios"],
    queryFn: () => powtoonBE.scenarios.list(),
  });

  // Prime the Tavus faces catalog so `ScenarioCard` can synchronously
  // resolve face thumbnails via the shared query cache. Only fires when
  // Tavus is available for this org AND at least one Tavus scenario is
  // present — avoids a wasted call for LA-only orgs.
  const tavusAvailable = getAvailableProviders(orgDomain).includes("tavus");
  const hasTavusScenarios = (data ?? []).some((s) => s.content?.provider === "tavus");
  useTavusFaces({ enabled: tavusAvailable && hasTavusScenarios });

  const myId = user?.powtoonUserId;
  const mine: Scenario[] = (data ?? []).filter((s) => myId != null && s.owner === myId);
  const company: Scenario[] = (data ?? []).filter((s) => myId == null || s.owner !== myId);

  // Company avatars only carry a numeric owner id — resolve the names in one
  // batched call so the cards can say who shared them. Best-effort: an org
  // where the lookup is restricted simply shows no line.
  const ownerIds = [...new Set(company.map((s) => s.owner).filter((v): v is number => v != null))];
  const { data: owners } = useQuery({
    queryKey: ["scenario-owners", ownerIds.slice().sort((a, b) => a - b).join(",")],
    queryFn: () => resolveUsers(ownerIds),
    enabled: ownerIds.length > 0,
    staleTime: 10 * 60_000,
  });

  return (
    <main className="mx-auto max-w-6xl px-6 py-8">
      {isLoading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (
        <div className="space-y-12">
          <h1 className="text-3xl font-bold text-foreground">Powtoon Interactive Live Avatars</h1>
          <section>
            <div className="mb-6 flex items-center justify-between gap-4">
              <h2 className="text-2xl font-bold text-foreground">Owned by me</h2>
              {canCreate && (
                <div className="flex items-center gap-2">
                  {canImport && (
                  <Button
                    variant="outline"
                    size="lg"
                    className="gap-2 h-12 px-4 text-base"
                    onClick={() => setImportOpen(true)}
                  >
                    <Download className="h-5 w-5" />
                    Import from old studio
                  </Button>
                  )}
                  <Link to="/trainer/new">
                    <Button size="lg" className="gap-2 h-12 px-6 text-base">
                      <Plus className="h-5 w-5" />
                      Create new Live Avatar
                    </Button>
                  </Link>
                </div>
              )}
            </div>
            {mine.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center animate-slide-up">
                <div className="mb-6 flex h-24 w-24 items-center justify-center rounded-3xl bg-accent">
                  <Sparkles className="h-12 w-12 text-primary" />
                </div>
                <h2 className="text-2xl font-bold text-foreground">No live avatars yet</h2>
                <p className="mt-2 max-w-sm text-muted-foreground">
                  Use “Add live avatar” at the top of the page to create your first one.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 animate-slide-up">
                {mine.map((s) => <ScenarioCard key={s.id} scenario={s} />)}
              </div>
            )}
          </section>

          <section>
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-foreground">Owned by others in my company</h2>
            </div>
            {company.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6">
                No shared live avatars from your company yet.
              </p>
            ) : (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 animate-slide-up">
                {company.map((s) => (
                  <ScenarioCard
                    key={s.id}
                    scenario={s}
                    readOnly
                    owner={s.owner != null ? owners?.[String(s.owner)] ?? null : null}
                  />
                ))}
              </div>
            )}
          </section>
        </div>
      )}
      {canImport && (
      <ImportLegacyModal
        open={importOpen}
        onOpenChange={setImportOpen}
        onImported={() => queryClient.invalidateQueries({ queryKey: ["scenarios"] })}
      />
      )}
    </main>
  );
}